import { useContext, useEffect, useMemo, useState } from "react";
import {
  BotFormType,
  ChartTabs,
  Error,
  Steps,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { BOT_FORM_TABS_ORDER } from "@shared/features/bots/BotForm/common/constants";
import { BotType, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

const useBotForm = () => {
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const {
    checkedFields,
    errors,
    botFormType,
    botId,
    isFetching,
    bulkActionValidBotsData,
    botType,
    isAdvancedView,
    tab,
    setTab,
    isBacktesting,
    isChannelConfiguration,
  } = useContext(BotFormContext)!;
  const isGridBot = botType === BotType.GRID;
  const isHedgingBot = botType === BotType.HEDGING;
  const isDcaBot = botType === BotType.ASAP;
  const isBulkEdit = botFormType === BotFormType.BULK_EDIT;
  const isBulkAction = botFormType === BotFormType.BULK_CREATE || isBulkEdit;
  const [chartTab, setChartTab] = useState(ChartTabs.CHART);

  useEffect(() => {
    if (!isHedgingBot && !isAdvancedView && tab !== Steps.GENERAL) {
      setTab(Steps.GENERAL);
    }
  }, [isAdvancedView, isHedgingBot]);

  useEffect(() => {
    if (isDcaBot) {
      if (isBacktesting && chartTab !== ChartTabs.BACKTESTING_RESULTS) {
        setChartTab(ChartTabs.BACKTESTING_RESULTS);
      } else if (!isBacktesting && chartTab === ChartTabs.BACKTESTING_RESULTS) {
        setChartTab(ChartTabs.CHART);
      }
    }
  }, [isDcaBot, isBacktesting]);

  const tabsOrder = useMemo(() => {
    let tabsOrder = BOT_FORM_TABS_ORDER;
    if (isHedgingBot) {
      return [Steps.GENERAL, Steps.ENTRIES];
    }
    if (isGridBot) {
      tabsOrder = BOT_FORM_TABS_ORDER.filter(
        (tab) => tab !== Steps.TAKE_PROFITS
      );
    }
    return tabsOrder;
  }, [isGridBot, isHedgingBot]);

  const tabsWithErrors = useMemo(
    () =>
      new Set(
        Object.values(Steps).filter((tab) => {
          const toggleField = TAB_TO_TOGGLE_FIELD[tab];
          if (toggleField && !checkedFields.has(toggleField)) return false;
          const errorTypes = TAB_TO_ERROR_TYPES[tab];
          return errorTypes?.some((error) => errors.includes(error));
        })
      ),
    [checkedFields, errors]
  );

  const tabsOptions = useMemo(
    () =>
      tabsOrder.map((tab) => {
        let displayText: string = t(tab);
        if (isGridBot && tab === Steps.ENTRIES) displayText = t("tabs.grids");
        if (isHedgingBot && tab === Steps.ENTRIES)
          displayText = t("labels.hedging");
        return { tab, displayText };
      }),
    [isGridBot, isHedgingBot, isChannelConfiguration, i18n.language]
  );

  const tabsData = {
    tabsOrder,
    tabsWithErrors,
    value: tab,
    onChange: setTab,
    options: tabsOptions,
  };

  const summaryProps = {
    copiedFrom: botFormType === BotFormType.COPY_PUBLIC ? botId : undefined,
    botId: botFormType === BotFormType.EDIT ? botId : undefined,
    disabled:
      (isBulkAction && !bulkActionValidBotsData?.length) || errors.length > 0,
    isFetching,
    tabsWithErrors: tabsData.tabsWithErrors,
  };

  return {
    tabsData,
    summaryProps,
    isFetching,
    chartTab,
    setChartTab,
  };
};

export default useBotForm;

const TAB_TO_ERROR_TYPES: { [key in Steps]?: Error[] } = {
  [Steps.GENERAL]: [
    Error.GENERAL,
    Error.BOT_NAME,
    Error.GENERAL_AMOUNT,
    Error.INSUFFICIENT_AMOUNT,
    Error.INVALID_LEVERAGE,
    Error.SUB_GROUP,
    Error.ALTERNATIVE_USD_PAIRS,
  ],
  [Steps.ENTRIES]: [
    Error.ENTRY_STRATEGY,
    Error.MIN_ENTRY_ORDER_AMOUNT,
    Error.ACTIVE_ENTRIES_AMOUNT,
    Error.ENTRIES_RATIO_SUM,
    Error.ENTRY_PRICES,
    Error.GRIDS_DATA,
    Error.CONSECUTIVE_GRIDS_PRICE_DISTANCE,
  ],
  [Steps.TAKE_PROFITS]: [
    Error.DEFAULT_TAKE_PROFIT,
    Error.TAKE_PROFIT_STRATEGY,
    Error.TAKE_PROFIT_PRICES,
  ],
  [Steps.STOP]: [Error.DEFAULT_STOP, Error.GENERAL_STOP],
  [Steps.ADVANCED]: [
    Error.GENERAL_ADVANCED,
    Error.ALTERNATIVE_USD_PAIRS,
    Error.KEYWORD_TO_SYMBOL,
  ],
};
const TAB_TO_TOGGLE_FIELD: { [key in Steps]?: ToggleField } = {
  [Steps.ENTRIES]: ToggleField.ENTRIES,
};
